import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", service_type: "usg", code: "", price: "", gender_applicability: "all", is_outsource: false, active: true };

export default function LabServices() {
  const { can } = useAuth();
  const manage = can("lab.services.manage");
  const [items, setItems] = useState(null);
  const [types, setTypes] = useState([]);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try { const { data } = await api.get("/lab/services", { params: { service_type: filter || undefined } }); setItems(data.items); setTypes(data.service_types); }
    catch (e) { setError(apiError(e)); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setFormError(""); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s, price: String(s.price ?? "") }); setFormError(""); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError("Service name is required."); return; }
    if (!(Number(form.price) > 0)) { setFormError("Price must be greater than zero."); return; }
    setBusy(true); setFormError("");
    try {
      const payload = { ...form, price: Number(form.price) };
      if (editing) await api.patch(`/lab/services/${editing.service_id}`, payload);
      else await api.post("/lab/services", payload);
      toast.success(editing ? "Service updated." : "Service added."); setOpen(false); load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };
  const toggle = async (s) => { try { await api.patch(`/lab/services/${s.service_id}`, { active: !s.active }); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!can("lab.tests.view")) return <NoAccess what="diagnostic services" />;

  return (
    <div data-testid="lab-services-page">
      <PageHeader overline="Diagnostics" title="Diagnostic Services"
        subtitle="Configurable imaging & diagnostic services (USG, X-ray, CT, MRI, ECG, outsource, etc.) billable on any case."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-service-btn"><Plus className="h-4 w-4" /> Add service</button>} />
      <div className="mb-5"><select className={`${inputCls} max-w-xs`} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="service-type-filter"><option value="">All types</option>{types.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}</select></div>
      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="lab-services-table" columns={["Service", "Type", "Price (₹)", "Gender", "Outsource", "Status", manage ? "Actions" : ""]}
          rows={items} empty={<EmptyState title="No services yet" subtitle="Add USG/X-ray/CT and other diagnostic services." testid="lab-services-empty" />}
          renderRow={(s) => (
            <tr key={s.service_id} data-testid={`lab-service-row-${s.service_id}`}>
              <Td className="font-medium text-slate-900">{s.name}</Td>
              <Td className="text-slate-600 capitalize">{(s.service_type || "").replace("_", " ")}</Td>
              <Td className="text-slate-700">{Number(s.price).toLocaleString("en-IN")}</Td>
              <Td className="text-slate-600 capitalize">{s.gender_applicability}</Td>
              <Td className="text-slate-600">{s.is_outsource ? "Yes" : "—"}</Td>
              <Td><span className={`text-xs font-medium ${s.active ? "text-emerald-600" : "text-slate-400"}`}>{s.active ? "Active" : "Inactive"}</span></Td>
              {manage && <Td><div className="flex gap-2"><button onClick={() => openEdit(s)} className={ghostBtnCls} data-testid={`edit-service-${s.service_id}`}>Edit</button><button onClick={() => toggle(s)} className={ghostBtnCls} data-testid={`toggle-service-${s.service_id}`}>{s.active ? "Deactivate" : "Activate"}</button></div></Td>}
            </tr>
          )} />
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit service" : "Add service"} testid="lab-service-modal">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Service name *"><input className={inputCls} data-testid="service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Service type *"><select className={inputCls} data-testid="service-type" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}>{types.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}</select></Field>
            <Field label="Code"><input className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
            <Field label="Price (₹) *"><input type="number" min="1" className={inputCls} data-testid="service-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
            <Field label="Gender"><select className={inputCls} value={form.gender_applicability} onChange={(e) => setForm({ ...form, gender_applicability: e.target.value })}><option value="all">All</option><option value="male">Male</option><option value="female">Female</option></select></Field>
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-6"><input type="checkbox" checked={form.is_outsource} onChange={(e) => setForm({ ...form, is_outsource: e.target.checked })} data-testid="service-outsource" /> Outsource</label>
          </div>
          {formError && <p className="text-sm text-red-600" data-testid="service-form-error">{formError}</p>}
          <div className="flex justify-end gap-3"><button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button><button disabled={busy} className={primaryBtnCls} data-testid="save-service-btn">{busy ? "Saving…" : "Save"}</button></div>
        </form>
      </Modal>
    </div>
  );
}
